'use client';

/**
 * VitalsReporter
 *
 * Collects Core Web Vitals (LCP, CLS, INP, FCP, TTFB) via Next.js
 * useReportWebVitals and batches them to the backend observability API.
 *
 * Design principles (mirrors errorReporter.ts):
 * - Fire-and-forget: flush failures are silently swallowed
 * - Uses raw fetch() / sendBeacon (not api() helper) to avoid circular loops
 * - Buffers metrics and flushes at threshold (5) or on beforeunload via sendBeacon
 * - SSR-safe: all window/navigator access is guarded
 * - Renders null — no UI
 *
 * Observability surfaces:
 * - Network tab: POST /api/_obs/vitals/batch visible in browser DevTools
 * - _frontend_vitals table in obs DB for stored vitals
 */

import { useEffect, useRef } from 'react';
import { useReportWebVitals } from 'next/web-vitals';
import { API_BASE } from '@/lib/api';

interface VitalReport {
  projectId: string | null;
  metricName: string;
  metricValue: number;
  rating: string | null;
  url: string | null;
  occurredAt: string; // ISO 8601
}

const BATCH_ENDPOINT = `${API_BASE}/api/_obs/vitals/batch`;
const FLUSH_THRESHOLD = 5;

/**
 * Extracts the projectId from the current URL.
 * Supports /projects/, /preview/, and /app/ path segments with optional locale prefix.
 */
function extractProjectId(): string | null {
  if (typeof window === 'undefined') return null;
  const match = window.location.pathname.match(
    /^(?:\/[a-z]{2})?\/(?:projects|preview|app)\/([^/]+)/
  );
  return match?.[1] ?? null;
}

/**
 * Flush vitals to the backend using fetch (fire-and-forget).
 */
async function flushVitals(batch: VitalReport[]): Promise<void> {
  try {
    await fetch(BATCH_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(batch),
      keepalive: true,
    });
  } catch {
    // Fire-and-forget: silently swallow network errors
  }
}

/**
 * Flush vitals synchronously using sendBeacon — for beforeunload handlers.
 */
function flushVitalsSync(batch: VitalReport[]): void {
  if (batch.length === 0) return;
  const blob = new Blob([JSON.stringify(batch)], { type: 'application/json' });
  navigator.sendBeacon(BATCH_ENDPOINT, blob);
}

/**
 * VitalsReporter — collects Core Web Vitals and sends them to the obs pipeline.
 * Renders null; mount in the client tree via ErrorReporter in root layout.
 */
export function VitalsReporter() {
  // Stable mutable ref so the beforeunload handler sees the current buffer
  const bufferRef = useRef<VitalReport[]>([]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      if (bufferRef.current.length > 0) {
        flushVitalsSync(bufferRef.current);
        bufferRef.current = [];
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  useReportWebVitals((metric) => {
    const report: VitalReport = {
      projectId: extractProjectId(),
      metricName: metric.name,
      metricValue: metric.value,
      rating: metric.rating ?? null,
      url: typeof window !== 'undefined' ? window.location.href : null,
      occurredAt: new Date().toISOString(),
    };

    bufferRef.current.push(report);

    if (bufferRef.current.length >= FLUSH_THRESHOLD) {
      const batch = bufferRef.current.splice(0);
      void flushVitals(batch);
    }
  });

  return null;
}
